<template>
  <div class="survey-management-page">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex align-items-center justify-content-between">
            <h4 class="card-title mb-0">
              <i class="bi bi-clipboard-check me-2"></i>Manage Survey Questionnaires
            </h4>
            <div class="d-flex gap-2">
              <div class="d-flex align-items-center gap-2">
                <label class="form-label mb-0">Show</label>
                <select class="form-select form-select-sm" v-model="entriesPerPage" style="width: auto;">
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
              <button class="btn btn-primary" @click="showCreateModal = true">
                <i class="bi bi-plus me-2"></i>Add Survey Questionnaire
              </button>
            </div>
          </div>
          <div class="card-body">
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
            <div v-else>
              <!-- Search -->
              <div class="row mb-4">
                <div class="col-md-6 ms-auto">
                  <div class="d-flex align-items-center gap-2">
                    <label class="form-label mb-0">Search:</label>
                    <input type="text" class="form-control form-control-sm" v-model="searchTerm" placeholder="Search surveys...">
                  </div>
                </div>
              </div>
              <!-- Surveys Table -->
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead>
                  <tr>
                    <th>Title</th>
                    <th>Created</th>
                    <th>Updated</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-if="surveys.length === 0">
                    <td colspan="6" class="text-center text-muted py-4">
                      No surveys found. Click "Add Survey Questionnaire" to create your first survey.
                    </td>
                  </tr>
                  <tr v-for="survey in paginatedSurveys" :key="survey.id">
                    <td>{{ survey.title }}</td>
                    <td>{{ formatDateTime(survey.created_at) }}</td>
                    <td>{{ formatDateTime(survey.updated_at) }}</td>
                    <td>
                      <span class="badge" :class="getStatusBadgeClass(survey.deleted_at ? 'Inactive' : survey.status)">
                        {{ survey.deleted_at ? 'Inactive' : survey.status }}
                      </span>
                      <button v-if="survey.deleted_at"
                              class="btn btn-sm btn-outline-info ms-2"
                              @click="restoreSurvey(survey)">
                        Restore
                      </button>
                    </td>
                    <td>
                      <div class="dropdown">
                        <button class="btn btn-sm btn-outline-secondary dropdown-toggle"
                                type="button"
                                data-bs-toggle="dropdown"
                                :disabled="survey.status === 'Inactive'">
                          <i class="bi bi-three-dots"></i>
                        </button>
                        <ul class="dropdown-menu">
                          <li><a class="dropdown-item" href="#" @click="editSurveyModal(survey)">Edit</a></li>
                          <li><a class="dropdown-item" href="#" @click="previewSurveyModal(survey)">Preview</a></li>
                          <li><a class="dropdown-item" href="#" @click="manageSurvey(survey)">Manage</a></li>
                          <li><a class="dropdown-item" href="#" @click="checkResults(survey)">Check results</a></li>
                          <li><a class="dropdown-item" href="#" @click="closeSurvey(survey)" v-if="survey.status === 'Active'">Close Survey</a></li>
                          <li><hr class="dropdown-divider"></li>
                          <li><a class="dropdown-item text-danger" href="#" @click="deleteSurvey(survey)">Delete</a></li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <!-- Pagination -->
              <div class="d-flex justify-content-between align-items-center mt-4">
                <div class="text-muted">
                  Showing {{ startEntry }} to {{ endEntry }} of {{ filteredSurveys.length }} entries
                </div>
                <nav>
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <a class="page-link" href="#" @click.prevent="currentPage--">Previous</a>
                    </li>
                    <li class="page-item"
                        v-for="page in totalPages"
                        :key="page"
                        :class="{ active: currentPage === page }">
                      <a class="page-link" href="#" @click.prevent="currentPage = page">{{ page }}</a>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <a class="page-link" href="#" @click.prevent="currentPage++">Next</a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Survey Modal -->
    <div class="modal fade" :class="{ show: showCreateModal, 'd-block': showCreateModal }" v-if="showCreateModal"
         data-bs-backdrop="static"
         data-bs-keyboard="false">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h4 class="modal-title text-muted">
              Add New Survey
            </h4>
            <button type="button" class="btn-close" @click="showCreateModal = false"></button>
          </div>
          <div class="modal-body">
            <!-- Survey Basic Info -->
            <div class="mb-4">
              <div class="mb-3">
                <input type="text"
                       class="form-control form-control-lg"
                       v-model="newSurvey.title"
                       placeholder="Title"
                       style="border: 2px solid #dee2e6; border-radius: 5px; font-weight: 500; padding: 0.75rem;">
              </div>
              <div class="mb-3">
                <textarea class="form-control"
                          rows="4"
                          v-model="newSurvey.description"
                          placeholder="Description"
                          style="border: 2px solid #dee2e6; resize: vertical;"></textarea>
              </div>
              <!-- Action Buttons -->
              <div class="d-flex gap-2 mb-4">
                <button type="button" class="btn btn-warning text-white" @click="cancelSurvey">
                  Cancel
                </button>
                <button type="button" class="btn btn-info text-white" @click="saveSurvey" :disabled="creating">
                  {{ creating ? 'Saving...' : 'Save Survey' }}
                </button>
              </div>
            </div>
            <!-- Survey Structure -->
            <div class="survey-structure">
              <h4 class="text-muted mb-4">Survey Structure</h4>
              <!-- Questions List -->
              <div v-for="(question, index) in newSurvey.questions" :key="question.id" class="question-block mb-4">
                <div class="d-flex justify-content-between align-items-start mb-3">
                  <h5 class="text-muted">Question {{ index + 1 }}</h5>
                  <div class="d-flex gap-2">
                    <input type="number"
                           class="form-control form-control-sm"
                           v-model="question.order"
                           placeholder="Survey order"
                           style="width: 120px;">
                    <button class="btn btn-sm btn-outline-danger" @click="removeQuestion(index)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
                <!-- Question Text -->
                <div class="mb-3">
                  <label class="form-label text-muted">Question Text</label>
                  <input type="text"
                         class="form-control"
                         v-model="question.text"
                         placeholder="Enter your question here">
                </div>
                <!-- Question Type and Required -->
                <div class="row mb-3">
                  <div class="col-md-8">
                    <label class="form-label text-muted">Question Type</label>
                    <select class="form-select" v-model="question.type" @change="onQuestionTypeChange(question)">
                      <option value="">Select Question Type</option>
                      <option value="multiple_choice_single">Multiple Choice, Single Answer</option>
                      <option value="multiple_choice_multiple">Multiple Choice, Multiple Answers</option>
                      <option value="single_line_text">Single Line of Text</option>
                      <option value="multiple_line_text">Multiple Lines of Text</option>
                    </select>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label text-muted">Required</label>
                    <select class="form-select" v-model="question.required">
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                </div>
                <!-- Options for Multiple Choice Questions -->
                <div v-if="question.type && (question.type === 'multiple_choice_single' || question.type === 'multiple_choice_multiple' || question.type === 'dropdown')" class="mb-3">
                  <label class="form-label text-muted">Options</label>
                  <div v-for="(option, optionIndex) in question.options" :key="optionIndex" class="d-flex gap-2 mb-2">
                    <input type="text"
                           class="form-control"
                           v-model="question.options[optionIndex]"
                           placeholder="Enter option">
                    <button class="btn btn-sm btn-outline-danger" @click="removeOption(question, optionIndex)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                  <button class="btn btn-success btn-sm" @click="addOption(question)">
                    Add Option
                  </button>
                  <!-- Others Option -->
                  <div class="mt-3">
                    <div class="d-flex justify-content-between align-items-center">
                      <label class="form-label text-muted mb-0">Other:</label>
                      <select class="form-select" v-model="question.allowOthers" style="width: auto;">
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </div>
                  </div>
                </div>
                <!-- Rating Scale Options -->
                <div v-if="question.type === 'rating_scale'" class="mb-3">
                  <div class="row">
                    <div class="col-md-6">
                      <label class="form-label text-muted">Scale Range</label>
                      <div class="d-flex gap-2 align-items-center">
                        <input type="number" class="form-control" v-model="question.scaleMin" placeholder="Min" min="1">
                        <span>to</span>
                        <input type="number" class="form-control" v-model="question.scaleMax" placeholder="Max" max="10">
                      </div>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label text-muted">Scale Labels</label>
                      <div class="d-flex gap-2">
                        <input type="text" class="form-control" v-model="question.scaleMinLabel" placeholder="Min label">
                        <input type="text" class="form-control" v-model="question.scaleMaxLabel" placeholder="Max label">
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Text Input Options -->
                <div v-if="question.type === 'single_line_text' || question.type === 'multiple_line_text'" class="mb-3">
                  <div class="row">
                    <div class="col-md-6">
                      <label class="form-label text-muted">Placeholder Text</label>
                      <input type="text" class="form-control" v-model="question.placeholder" placeholder="Enter placeholder text">
                    </div>
                    <div class="col-md-6" v-if="question.type === 'single_line_text'">
                      <label class="form-label text-muted">Character Limit</label>
                      <input type="number" class="form-control" v-model="question.charLimit" placeholder="Max characters">
                    </div>
                  </div>
                </div>
                <hr class="my-4">
              </div>
              <!-- Add New Question Button -->
              <button class="btn btn-success" @click="addNewQuestion">
                Add New Question
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Survey Modal -->
    <div class="modal fade" :class="{ show: showEditModal, 'd-block': showEditModal }" v-if="showEditModal"
         data-bs-backdrop="static"
         data-bs-keyboard="false">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h4 class="modal-title text-muted">
              Edit Survey - {{ editingSurvey?.title }}
            </h4>
            <button type="button" class="btn-close" @click="cancelEditSurvey"></button>
          </div>
          <div class="modal-body">
            <!-- Survey Basic Info -->
            <div class="mb-4">
              <div class="mb-3">
                <input type="text"
                       class="form-control form-control-lg"
                       v-model="editSurvey.title"
                       placeholder="Title"
                       style="border: 2px solid #dee2e6; border-radius: 5px; font-weight: 500; padding: 0.75rem;">
              </div>
              <div class="mb-3">
                <textarea class="form-control"
                          rows="4"
                          v-model="editSurvey.description"
                          placeholder="Description"
                          style="border: 2px solid #dee2e6; resize: vertical;"></textarea>
              </div>
              <!-- Action Buttons -->
              <div class="d-flex gap-2 mb-4">
                <button type="button" class="btn btn-warning text-white" @click="cancelEditSurvey">
                  Cancel
                </button>
                <button type="button" class="btn btn-info text-white" @click="updateSurveyWithValidation" :disabled="editing">
                  {{ editing ? 'Updating...' : 'Update Survey' }}
                </button>
              </div>
            </div>
            <!-- Survey Structure -->
            <div class="survey-structure">
              <h4 class="text-muted mb-4">Survey Structure</h4>
              <!-- Questions List -->
              <div v-for="(question, index) in editSurvey.questions" :key="question.id" class="question-block mb-4">
                <div class="d-flex justify-content-between align-items-start mb-3">
                  <h5 class="text-muted">Question {{ index + 1 }}</h5>
                  <div class="d-flex gap-2">
                    <input type="number"
                           class="form-control form-control-sm"
                           v-model="question.order"
                           placeholder="Survey order"
                           style="width: 120px;">
                    <button class="btn btn-sm btn-outline-danger" @click="removeEditQuestion(index)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
                <!-- Question Text -->
                <div class="mb-3">
                  <label class="form-label text-muted">Question Text</label>
                  <input type="text"
                         class="form-control"
                         v-model="question.text"
                         placeholder="Enter your question here">
                </div>
                <!-- Question Type and Required -->
                <div class="row mb-3">
                  <div class="col-md-8">
                    <label class="form-label text-muted">Question Type</label>
                    <select class="form-select" v-model="question.type" @change="onEditQuestionTypeChange(question)">
                      <option value="">Select Question Type</option>
                      <option value="multiple_choice_single">Multiple Choice, Single Answer</option>
                      <option value="multiple_choice_multiple">Multiple Choice, Multiple Answers</option>
                      <option value="single_line_text">Single Line of Text</option>
                      <option value="multiple_line_text">Multiple Lines of Text</option>
                    </select>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label text-muted">Required</label>
                    <select class="form-select" v-model="question.required">
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                </div>
                <!-- Options for Multiple Choice Questions -->
                <div v-if="question.type && (question.type === 'multiple_choice_single' || question.type === 'multiple_choice_multiple' || question.type === 'dropdown')" class="mb-3">
                  <label class="form-label text-muted">Options</label>
                  <div v-for="(option, optionIndex) in question.options" :key="optionIndex" class="d-flex gap-2 mb-2">
                    <input type="text"
                           class="form-control"
                           v-model="question.options[optionIndex]"
                           placeholder="Enter option">
                    <button class="btn btn-sm btn-outline-danger" @click="removeEditOption(question, optionIndex)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                  <button class="btn btn-success btn-sm" @click="addEditOption(question)">
                    Add Option
                  </button>
                  <!-- Others Option -->
                  <div class="mt-3">
                    <div class="d-flex justify-content-between align-items-center">
                      <label class="form-label text-muted mb-0">Other:</label>
                      <select class="form-select" v-model="question.allowOthers" style="width: auto;">
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </div>
                  </div>
                </div>
                <!-- Rating Scale Options -->
                <div v-if="question.type === 'rating_scale'" class="mb-3">
                  <div class="row">
                    <div class="col-md-6">
                      <label class="form-label text-muted">Scale Range</label>
                      <div class="d-flex gap-2 align-items-center">
                        <input type="number" class="form-control" v-model="question.scaleMin" placeholder="Min" min="1">
                        <span>to</span>
                        <input type="number" class="form-control" v-model="question.scaleMax" placeholder="Max" max="10">
                      </div>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label text-muted">Scale Labels</label>
                      <div class="d-flex gap-2">
                        <input type="text" class="form-control" v-model="question.scaleMinLabel" placeholder="Min label">
                        <input type="text" class="form-control" v-model="question.scaleMaxLabel" placeholder="Max label">
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Text Input Options -->
                <div v-if="question.type === 'single_line_text' || question.type === 'multiple_line_text'" class="mb-3">
                  <div class="row">
                    <div class="col-md-6">
                      <label class="form-label text-muted">Placeholder Text</label>
                      <input type="text" class="form-control" v-model="question.placeholder" placeholder="Enter placeholder text">
                    </div>
                    <div class="col-md-6" v-if="question.type === 'single_line_text'">
                      <label class="form-label text-muted">Character Limit</label>
                      <input type="number" class="form-control" v-model="question.charLimit" placeholder="Max characters">
                    </div>
                  </div>
                </div>
                <hr class="my-4">
              </div>
              <!-- Add New Question Button -->
              <button class="btn btn-success" @click="addNewEditQuestion">
                Add New Question
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Enhanced Preview Survey Modal -->
    <div class="modal fade" :class="{ show: showPreviewModal, 'd-block': showPreviewModal }" v-if="showPreviewModal"
         data-bs-backdrop="static"
         data-bs-keyboard="false">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-eye me-2"></i>Survey Preview
            </h5>
            <button type="button" class="btn-close" @click="showPreviewModal = false"></button>
          </div>
          <div class="modal-body">
            <div v-if="previewLoading" class="text-center py-4">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading preview...</span>
              </div>
            </div>
            <div v-else-if="previewSurveyData" class="survey-preview-container">
              <div class="survey-preview bg-white p-4 rounded shadow-sm">
                <!-- Survey Header -->
                <div class="survey-header mb-4 p-4 bg-light rounded">
                  <h3 class="survey-title mb-3">{{ previewSurveyData.title }}</h3>
                  <p class="survey-description text-muted mb-0" v-if="previewSurveyData.description">
                    {{ previewSurveyData.description }}
                  </p>
                </div>
                <!-- Survey Questions -->
                <div class="survey-questions">
                  <div v-for="(question, index) in previewSurveyData.questions"
                       :key="question.id"
                       class="question-preview mb-4 p-3 border rounded bg-white">
                    <div class="question-header mb-3">
                      <label class="question-label fw-semibold d-block">
                        {{ index + 1 }}. {{ question.text }}
                        <span v-if="question.required" class="text-danger ms-1">*</span>
                      </label>
                    </div>
                    <div class="question-input">
                      <!-- Single Line Text -->
                      <div v-if="question.type === 'single_line_text'">
                        <input type="text"
                               class="form-control"
                               :placeholder="question.placeholder || 'Enter your answer'"
                               :maxlength="question.charLimit"
                               disabled>
                        <small v-if="question.charLimit" class="text-muted">
                          Maximum {{ question.charLimit }} characters
                        </small>
                      </div>
                      <!-- Multiple Line Text -->
                      <div v-else-if="question.type === 'multiple_line_text'">
                        <textarea class="form-control"
                                  rows="4"
                                  :placeholder="question.placeholder || 'Enter your answer'"
                                  disabled></textarea>
                      </div>
                      <!-- Single Choice Radio -->
                      <div v-else-if="question.type === 'multiple_choice_single'">
                        <div v-for="option in question.options" :key="option" class="form-check mb-2">
                          <input class="form-check-input"
                                 type="radio"
                                 :name="`preview_q${question.id}`"
                                 disabled>
                          <label class="form-check-label">{{ option }}</label>
                        </div>
                        <div v-if="question.allowOthers" class="form-check">
                          <input class="form-check-input"
                                 type="radio"
                                 :name="`preview_q${question.id}`"
                                 disabled>
                          <label class="form-check-label">Other:</label>
                          <input type="text" class="form-control form-control-sm mt-1 ms-4" placeholder="Please specify" disabled>
                        </div>
                      </div>
                      <!-- Multiple Choice Checkbox -->
                      <div v-else-if="question.type === 'multiple_choice_multiple'">
                        <div v-for="option in question.options" :key="option" class="form-check mb-2">
                          <input class="form-check-input" type="checkbox" disabled>
                          <label class="form-check-label">{{ option }}</label>
                        </div>
                        <div v-if="question.allowOthers" class="form-check">
                          <input class="form-check-input" type="checkbox" disabled>
                          <label class="form-check-label">Other:</label>
                          <input type="text" class="form-control form-control-sm mt-1 ms-4" placeholder="Please specify" disabled>
                        </div>
                      </div>
                      <!-- Dropdown -->
                      <div v-else-if="question.type === 'dropdown'">
                        <select class="form-select" disabled>
                          <option value="">Select an option...</option>
                          <option v-for="option in question.options" :key="option">{{ option }}</option>
                          <option v-if="question.allowOthers" value="other">Other</option>
                        </select>
                      </div>
                      <!-- Rating Scale -->
                      <div v-else-if="question.type === 'rating_scale'">
                        <div class="rating-scale-container">
                          <div class="d-flex justify-content-between align-items-center mb-2">
                            <span class="text-muted small">{{ question.scaleMinLabel || question.scaleMin }}</span>
                            <span class="text-muted small">{{ question.scaleMaxLabel || question.scaleMax }}</span>
                          </div>
                          <div class="d-flex justify-content-center gap-2">
                            <label v-for="n in (question.scaleMax - question.scaleMin + 1)"
                                   :key="n"
                                   class="rating-option">
                              <input type="radio"
                                     :name="`preview_rating_${question.id}`"
                                     :value="question.scaleMin + n - 1"
                                     class="rating-input"
                                     disabled>
                              <span class="rating-label">{{ question.scaleMin + n - 1 }}</span>
                            </label>
                          </div>
                        </div>
                      </div>
                      <!-- Yes/No -->
                      <div v-else-if="question.type === 'yes_no'">
                        <div class="form-check">
                          <input class="form-check-input" type="radio" :name="`preview_q${question.id}`" disabled>
                          <label class="form-check-label">Yes</label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="radio" :name="`preview_q${question.id}`" disabled>
                          <label class="form-check-label">No</label>
                        </div>
                      </div>
                      <!-- Date -->
                      <div v-else-if="question.type === 'date'">
                        <input type="date" class="form-control" disabled>
                      </div>
                      <!-- Email -->
                      <div v-else-if="question.type === 'email'">
                        <input type="email" class="form-control" placeholder="Enter email address" disabled>
                      </div>
                      <!-- Number -->
                      <div v-else-if="question.type === 'number'">
                        <input type="number" class="form-control" placeholder="Enter number" disabled>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Survey Footer -->
                <div class="survey-footer text-center mt-4 pt-4 border-top">
                  <button class="btn btn-primary btn-lg px-5" disabled style="color: #fff">
                    <i class="bi bi-check-circle me-2"></i>Submit Survey
                  </button>
                  <p class="text-muted small mt-2 mb-0">This is a preview - responses cannot be submitted</p>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showPreviewModal = false">Close Preview</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Enhanced Manage Invitations Modal -->
    <div class="modal fade" :class="{ show: showManageModal, 'd-block': showManageModal }" v-if="showManageModal"
         data-bs-backdrop="static"
         data-bs-keyboard="false">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" style="margin-right: 15px">
              <i class="bi bi-people me-2"></i>Manage Survey Invitations
            </h5>
            <div class="d-flex gap-2">
              <button type="button"
                      class="btn btn-success"
                      @click="sendSurveyInvitations"
                      :disabled="invitedUsers.length === 0 || sending">
                <i class="bi bi-send me-2"></i>{{ sending ? 'Sending...' : 'Send Survey to Invited Users' }}
              </button>
              <button type="button" class="btn btn-outline-info" @click="goBackToSurveys">
                Back to Survey List
              </button>
            </div>
            <button type="button" class="btn-close" @click="showManageModal = false"></button>
          </div>
          <div class="modal-body">
            <div v-if="loadingManagement" class="text-center py-4">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading management data...</span>
              </div>
            </div>
            <div v-else-if="selectedSurvey">
              <!-- Survey Info Header -->
              <div class="survey-info-header mb-4 p-3 bg-light rounded">
                <h6 class="mb-2">
                  <i class="bi bi-clipboard-check me-2"></i>Survey: {{ selectedSurvey.title }}
                </h6>
                <div class="row text-center">
                  <div class="col-md-4">
                    <div class="stat-item">
                      <div class="stat-number text-primary">{{ availableUsers.length }}</div>
                      <div class="stat-label text-muted small">Available Users</div>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="stat-item">
                      <div class="stat-number text-success">{{ invitedUsers.length }}</div>
                      <div class="stat-label text-muted small">Invited Users</div>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="stat-item">
                      <div class="stat-number text-info">{{ getSelectedCount('available') + getSelectedCount('invited') }}</div>
                      <div class="stat-label text-muted small">Selected</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
                <!-- Available Users Column -->
                <div class="col-md-6">
                  <div class="user-list-container">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <h6 class="mb-0">
                        <i class="bi bi-person-plus me-2"></i>Available Users ({{ availableUsers.length }})
                      </h6>
                      <div class="btn-group btn-group-sm">
                        <button type="button" class="btn btn-outline-primary" @click="selectAllAvailable">
                          Select All
                        </button>
                        <button type="button" class="btn btn-outline-secondary" @click="unselectAllAvailable">
                          Clear
                        </button>
                      </div>
                    </div>
                    <!-- Search Available Users -->
                    <div class="mb-3">
                      <input type="text"
                             class="form-control form-control-sm"
                             v-model="availableUsersSearch"
                             placeholder="Search available users...">
                    </div>
                    <!-- Available Users List -->
                    <div class="user-list border rounded p-3" style="height: 350px; overflow-y: auto;">
                      <div v-if="filteredAvailableUsers.length === 0" class="text-center text-muted py-4">
                        <i class="bi bi-person-x fs-1 d-block mb-2"></i>
                        No available users found
                      </div>
                      <div v-else>
                        <div class="form-check user-item mb-2"
                             v-for="user in filteredAvailableUsers"
                             :key="user.id">
                          <input class="form-check-input"
                                 type="checkbox"
                                 v-model="user.selected"
                                 :id="'available-' + user.id">
                          <label class="form-check-label user-label" :for="'available-' + user.id">
                            <div class="d-flex align-items-center">
                              <div class="user-avatar me-2">
                                <i class="bi bi-person-circle fs-4 text-muted"></i>
                              </div>
                              <div class="user-info">
                                <div class="user-name">{{ user.name }}</div>
                                <div class="user-email text-muted small">{{ user.email }}</div>
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                    <!-- Invite Button -->
                    <div class="text-center mt-3">
                      <button class="btn btn-success"
                              @click="inviteSelected"
                              :disabled="!hasSelectedAvailable || managing">
                        <i class="bi bi-arrow-right me-2"></i>
                        {{ managing ? 'Inviting...' : `Invite Selected (${getSelectedCount('available')})` }}
                      </button>
                    </div>
                  </div>
                </div>
                <!-- Invited Users Column -->
                <div class="col-md-6">
                  <div class="user-list-container">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <h6 class="mb-0">
                        <i class="bi bi-person-check me-2"></i>Invited Users ({{ invitedUsers.length }})
                      </h6>
                      <div class="btn-group btn-group-sm">
                        <button type="button" class="btn btn-outline-primary" @click="selectAllInvited">
                          Select All
                        </button>
                        <button type="button" class="btn btn-outline-secondary" @click="unselectAllInvited">
                          Clear
                        </button>
                      </div>
                    </div>
                    <!-- Search Invited Users -->
                    <div class="mb-3">
                      <input type="text"
                             class="form-control form-control-sm"
                             v-model="invitedUsersSearch"
                             placeholder="Search invited users...">
                    </div>
                    <!-- Invited Users List -->
                    <div class="user-list border rounded p-3" style="height: 350px; overflow-y: auto;">
                      <div v-if="filteredInvitedUsers.length === 0" class="text-center text-muted py-4">
                        <i class="bi bi-person-check fs-1 d-block mb-2"></i>
                        No invited users
                      </div>
                      <div v-else>
                        <div class="form-check user-item mb-2"
                             v-for="user in filteredInvitedUsers"
                             :key="user.id">
                          <input class="form-check-input"
                                 type="checkbox"
                                 v-model="user.selected"
                                 :id="'invited-' + user.id">
                          <label class="form-check-label user-label" :for="'invited-' + user.id">
                            <div class="d-flex align-items-center">
                              <div class="user-avatar me-2">
                                <i class="bi bi-person-check-fill fs-4 text-success"></i>
                              </div>
                              <div class="user-info">
                                <div class="user-name">{{ user.name }}</div>
                                <div class="user-email text-muted small">{{ user.email }}</div>
                                <div class="user-status">
                                  <span class="badge bg-success">Invited</span>
                                </div>
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                    <!-- Remove Button -->
                    <div class="text-center mt-3">
                      <button class="btn btn-danger"
                              @click="uninviteSelected"
                              :disabled="!hasSelectedInvited || managing">
                        <i class="bi bi-arrow-left me-2"></i>
                        {{ managing ? 'Removing...' : `Remove Selected (${getSelectedCount('invited')})` }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Close Survey Modal -->
    <div class="modal fade" :class="{ show: showCloseSurveyModal, 'd-block': showCloseSurveyModal }" v-if="showCloseSurveyModal"
         data-bs-backdrop="static"
         data-bs-keyboard="false">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-exclamation-triangle me-2"></i>Close Survey
            </h5>
            <button type="button" class="btn-close" @click="showCloseSurveyModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-warning">
              <i class="bi bi-exclamation-triangle me-2"></i>
              Do you want to close the survey to receive new answers?
            </div>
            <p>Once closed, participants will no longer be able to submit responses to this survey.</p>
            <div class="mb-3" v-if="surveyToClose">
              <strong>Survey:</strong> {{ surveyToClose.title }}
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showCloseSurveyModal = false">Cancel</button>
            <button type="button" class="btn btn-warning" @click="confirmCloseSurvey" :disabled="closing">
              <i class="bi bi-lock me-2"></i>{{ closing ? 'Closing...' : 'OK' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Survey Modal -->
    <div class="modal fade" :class="{ show: showDeleteModal, 'd-block': showDeleteModal }" v-if="showDeleteModal"
         data-bs-backdrop="static"
         data-bs-keyboard="false">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title text-danger">
              <i class="bi bi-exclamation-triangle me-2"></i>Delete Survey
            </h5>
            <button type="button" class="btn-close" @click="showDeleteModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-danger">
              <i class="bi bi-exclamation-triangle me-2"></i>
              <strong>Warning:</strong> This action will move the survey to deleted status. You can restore it later if needed.
            </div>
            <p>Are you sure you want to delete this survey?</p>
            <div class="mb-3" v-if="surveyToDelete">
              <strong>Survey:</strong> {{ surveyToDelete.title }}
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showDeleteModal = false">Cancel</button>
            <button type="button" class="btn btn-danger" @click="confirmDeleteSurvey" :disabled="deleting">
              <i class="bi bi-trash me-2"></i>{{ deleting ? 'Deleting...' : 'Delete Survey' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Restore Survey Modal -->
    <div class="modal fade" :class="{ show: showRestoreModal, 'd-block': showRestoreModal }" v-if="showRestoreModal" data-bs-backdrop="static"
         data-bs-keyboard="false">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title text-success">
              <i class="bi bi-arrow-clockwise me-2"></i>Restore Survey
            </h5>
            <button type="button" class="btn-close" @click="showRestoreModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-success">
              <i class="bi bi-info-circle me-2"></i>
              This will restore the survey and make it available again.
            </div>
            <p>Are you sure you want to restore this survey?</p>
            <div class="mb-3" v-if="surveyToRestore">
              <strong>Survey:</strong> {{ surveyToRestore.title }}
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showRestoreModal = false">Cancel</button>
            <button type="button" class="btn btn-success" @click="confirmRestoreSurvey" :disabled="restoring">
              <i class="bi bi-arrow-clockwise me-2"></i>{{ restoring ? 'Restoring...' : 'Restore Survey' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Results Modal -->
    <div class="modal fade" :class="{ show: showResultsModal, 'd-block': showResultsModal }" v-if="showResultsModal" data-bs-backdrop="static"
         data-bs-keyboard="false">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" style="margin-right: 15px">
              <i class="bi bi-bar-chart me-2"></i>Survey Results
            </h5>
            <div class="d-flex gap-2">
              <button type="button" class="btn btn-success" @click="exportResults">
                <i class="bi bi-download me-2"></i>Export as Excel
              </button>
              <button type="button" class="btn btn-outline-info" @click="goBackToSurveys">
                Back to Surveys
              </button>
            </div>
            <button type="button" class="btn-close" @click="showResultsModal = false"></button>
          </div>
          <div class="modal-body">
            <div v-if="loadingResults" class="text-center py-4">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading results...</span>
              </div>
            </div>
            <div v-else-if="selectedSurvey && surveyResults">
              <div class="row mb-4">
                <div class="col-md-6">
                  <div class="d-flex align-items-center gap-2">
                    <label class="form-label mb-0">Show</label>
                    <select class="form-select form-select-sm" style="width: auto;" v-model="resultsPerPage">
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="d-flex align-items-center gap-2 justify-content-end">
                    <label class="form-label mb-0">Search:</label>
                    <input type="text" class="form-control form-control-sm" v-model="resultsSearchTerm" placeholder="Search results..." style="width: 200px;">
                  </div>
                </div>
              </div>
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead>
                  <tr>
                    <th>PARTICIPANT</th>
                    <th>DATE SUBMITTED</th>
                    <th>STATUS</th>
                    <th>ACTIONS</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="result in paginatedResults" :key="result.id">
                    <td>
                      <div class="d-flex align-items-center">
                        <i class="bi bi-person-circle fs-4 text-muted me-2"></i>
                        <div>
                          <div>{{ result.user.name }}</div>
                          <small class="text-muted">{{ result.user.email }}</small>
                        </div>
                      </div>
                    </td>
                    <td>{{ formatDateTime(result.submitted_at) }}</td>
                    <td>
                      <span class="badge bg-success">Completed</span>
                    </td>
                    <td>
                      <button class="btn btn-sm btn-info" @click="viewIndividualAnswers(result)">
                        <i class="bi bi-eye me-1"></i>View Answers
                      </button>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <div class="d-flex justify-content-between align-items-center mt-4">
                <div class="text-muted">
                  Showing {{ resultsStartEntry }} to {{ resultsEndEntry }} of {{ filteredResults.length }} entries
                </div>
                <nav>
                  <ul class="pagination pagination-sm mb-0">
                    <li class="page-item" :class="{ disabled: resultsCurrentPage === 1 }">
                      <a class="page-link" href="#" @click.prevent="resultsCurrentPage--">Previous</a>
                    </li>
                    <li class="page-item"
                        v-for="page in resultsTotalPages"
                        :key="page"
                        :class="{ active: resultsCurrentPage === page }">
                      <a class="page-link" href="#" @click.prevent="resultsCurrentPage = page">{{ page }}</a>
                    </li>
                    <li class="page-item" :class="{ disabled: resultsCurrentPage === resultsTotalPages }">
                      <a class="page-link" href="#" @click.prevent="resultsCurrentPage++">Next</a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Individual Answers Modal -->
    <div class="modal fade" :class="{ show: showIndividualAnswersModal, 'd-block': showIndividualAnswersModal }" v-if="showIndividualAnswersModal"
         data-bs-backdrop="static"
         data-bs-keyboard="false">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" style="margin-right: 15px">
              <i class="bi bi-person-lines-fill me-2"></i>Individual Survey Response
            </h5>
            <div class="d-flex gap-2">
              <button type="button" class="btn btn-outline-info" @click="showIndividualAnswersModal = false; showResultsModal = true">
                <i class="bi bi-arrow-left me-2"></i>Back to Results
              </button>
            </div>
            <button type="button" class="btn-close" @click="closeIndividualAnswersModal"></button>
          </div>
          <div class="modal-body">
            <div v-if="loadingIndividualAnswers" class="text-center py-4">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading answers...</span>
              </div>
            </div>
            <div v-else-if="individualAnswers">
              <!-- User Info Header -->
              <div class="user-info-header mb-4 p-4 bg-light rounded">
                <div class="d-flex align-items-center">
                  <i class="bi bi-person-circle fs-1 text-muted me-3"></i>
                  <div>
                    <h5 class="mb-1">{{ individualAnswers.user.name }}</h5>
                    <p class="text-muted mb-1">{{ individualAnswers.user.email }}</p>
                    <small class="text-muted">
                      <i class="bi bi-calendar-check me-1"></i>
                      Submitted: {{ formatDateTime(individualAnswers.submitted_at) }}
                    </small>
                  </div>
                </div>
              </div>

              <!-- Survey Info -->
              <div class="survey-info-section mb-4 p-3 border rounded">
                <h6 class="text-primary mb-2">
                  <i class="bi bi-clipboard-check me-2"></i>{{ selectedSurvey.title }}
                </h6>
                <p class="text-muted mb-0" v-if="selectedSurvey.description">
                  {{ selectedSurvey.description }}
                </p>
              </div>

              <!-- Individual Answers Display -->
              <div class="answers-container">
                <h6 class="mb-4 text-secondary">
                  <i class="bi bi-chat-left-text me-2"></i>Survey Responses
                </h6>

                <div v-for="(questionData, index) in individualAnswers.questions"
                     :key="questionData.id"
                     class="answer-item mb-4 p-4 border rounded bg-white">

                  <!-- Question -->
                  <div class="question-section mb-3">
                    <div class="d-flex align-items-start">
                      <span class="question-number badge bg-primary me-3">{{ index + 1 }}</span>
                      <div class="flex-grow-1">
                        <h6 class="question-text mb-2">{{ questionData.text }}</h6>
                        <small class="question-type text-muted">
                          <i class="bi bi-gear me-1"></i>{{ getQuestionTypeLabel(questionData.type) }}
                          <span v-if="questionData.required" class="text-danger ms-2">
                            <i class="bi bi-asterisk"></i> Required
                          </span>
                        </small>
                      </div>
                    </div>
                  </div>

                  <!-- Answer -->
                  <div class="answer-section">
                    <div class="answer-label mb-2">
                      <i class="bi bi-chat-square-text me-2 text-success"></i>
                      <strong class="text-success">User's Answer:</strong>
                    </div>

                    <div class="answer-content p-3 rounded" :class="questionData.answer ? 'bg-light border' : 'bg-warning bg-opacity-10 border border-warning'">
                      <!-- Has Answer -->
                      <div v-if="questionData.answer && questionData.answer.answer_text">
                        <!-- Text Answers -->
                        <div v-if="questionData.type === 'single_line_text' || questionData.type === 'multiple_line_text' || questionData.type === 'text' || questionData.type === 'textarea'">
                          <div class="answer-text" style="white-space: pre-wrap;">{{ questionData.answer.answer_text }}</div>
                        </div>

                        <!-- Multiple Choice Single -->
                        <div v-else-if="questionData.type === 'multiple_choice_single' || questionData.type === 'radio'">
                          <div class="selected-option">
                            <i class="bi bi-check-circle-fill text-success me-2"></i>
                            <strong>{{ questionData.answer.answer_text }}</strong>
                          </div>
                          <!-- Show all options with selected one highlighted -->
                          <div class="mt-2">
                            <small class="text-muted">Available options were:</small>
                            <div class="options-list mt-1">
                              <div v-for="option in questionData.options"
                                   :key="option"
                                   class="option-item"
                                   :class="{ 'selected-option-highlight': option === questionData.answer.answer_text }">
                                <i class="bi" :class="option === questionData.answer.answer_text ? 'bi-dot text-success' : 'bi-circle text-muted'"></i>
                                {{ option }}
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Multiple Choice Multiple -->
                        <div v-else-if="questionData.type === 'multiple_choice_multiple' || questionData.type === 'checkbox'">
                          <div class="selected-options">
                            <div v-for="selectedOption in getSelectedOptions(questionData.answer.answer_text)"
                                 :key="selectedOption"
                                 class="selected-option mb-1">
                              <i class="bi bi-check-square-fill text-success me-2"></i>
                              <strong>{{ selectedOption }}</strong>
                            </div>
                          </div>
                          <!-- Show all options -->
                          <div class="mt-2">
                            <small class="text-muted">Available options were:</small>
                            <div class="options-list mt-1">
                              <div v-for="option in questionData.options"
                                   :key="option"
                                   class="option-item"
                                   :class="{ 'selected-option-highlight': getSelectedOptions(questionData.answer.answer_text).includes(option) }">
                                <i class="bi" :class="getSelectedOptions(questionData.answer.answer_text).includes(option) ? 'bi-check-square text-success' : 'bi-square text-muted'"></i>
                                {{ option }}
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Rating Scale -->
                        <div v-else-if="questionData.type === 'rating_scale'">
                          <div class="rating-display d-flex align-items-center">
                            <span class="rating-value badge bg-primary fs-6 me-3">{{ questionData.answer.answer_text }}</span>
                            <div class="rating-stars">
                              <i v-for="star in 5"
                                 :key="star"
                                 class="bi"
                                 :class="star <= parseInt(questionData.answer.answer_text) ? 'bi-star-fill text-warning' : 'bi-star text-muted'"></i>
                            </div>
                            <span class="ms-2 text-muted">out of 5</span>
                          </div>
                        </div>

                        <!-- Yes/No -->
                        <div v-else-if="questionData.type === 'yes_no'">
                          <div class="yes-no-answer">
                            <i class="bi" :class="questionData.answer.answer_text.toLowerCase() === 'yes' ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'"></i>
                            <strong class="ms-2" :class="questionData.answer.answer_text.toLowerCase() === 'yes' ? 'text-success' : 'text-danger'">
                              {{ questionData.answer.answer_text }}
                            </strong>
                          </div>
                        </div>

                        <!-- Date -->
                        <div v-else-if="questionData.type === 'date'">
                          <div class="date-answer">
                            <i class="bi bi-calendar-date me-2 text-info"></i>
                            <strong>{{ formatDate(questionData.answer.answer_text) }}</strong>
                          </div>
                        </div>

                        <!-- Email -->
                        <div v-else-if="questionData.type === 'email'">
                          <div class="email-answer">
                            <i class="bi bi-envelope me-2 text-info"></i>
                            <a :href="'mailto:' + questionData.answer.answer_text" class="text-primary">
                              {{ questionData.answer.answer_text }}
                            </a>
                          </div>
                        </div>

                        <!-- Number -->
                        <div v-else-if="questionData.type === 'number'">
                          <div class="number-answer">
                            <i class="bi bi-123 me-2 text-info"></i>
                            <strong>{{ questionData.answer.answer_text }}</strong>
                          </div>
                        </div>

                        <!-- Default/Other Types -->
                        <div v-else>
                          <div class="default-answer">{{ questionData.answer.answer_text }}</div>
                        </div>
                      </div>

                      <!-- No Answer -->
                      <div v-else class="no-answer">
                        <i class="bi bi-exclamation-triangle text-warning me-2"></i>
                        <em class="text-muted">No answer provided</em>
                        <span v-if="questionData.required" class="text-danger ms-2">
                          (This was a required question)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Summary Section -->
              <div class="response-summary mt-4 p-3 bg-opacity-10 rounded" style="background: #f0f0f0">
                <h6 class="text-info mb-2">
                  <i class="bi bi-bar-chart me-2"></i>Response Summary
                </h6>
                <div class="row text-center">
                  <div class="col-md-4">
                    <div class="summary-stat">
                      <div class="stat-number text-success">{{ getAnsweredCount() }}</div>
                      <div class="stat-label text-muted small">Answered</div>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="summary-stat">
                      <div class="stat-number text-warning">{{ getUnansweredCount() }}</div>
                      <div class="stat-label text-muted small">Unanswered</div>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="summary-stat">
                      <div class="stat-number text-info">{{ Math.round(getCompletionPercentage()) }}%</div>
                      <div class="stat-label text-muted small">Complete</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeIndividualAnswersModal">
              Close
            </button>
            <button type="button" class="btn btn-primary" @click="showIndividualAnswersModal = false; showResultsModal = true">
              <i class="bi bi-arrow-left me-2"></i>Back to All Results
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Toast Notification -->
    <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 9999;">
      <div class="toast" :class="{ show: toast.show, 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error' }" role="alert">
        <div class="toast-header" :class="{ 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error', 'text-white': true }">
          <i class="bi me-2" :class="toast.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'"></i>
          <strong class="me-auto">{{ toast.type === 'success' ? 'Success' : 'Error' }}</strong>
          <button type="button" class="btn-close btn-close-white" @click="toast.show = false"></button>
        </div>
        <div class="toast-body text-white">
          {{ toast.message }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SurveyService from '@/services/surveys/SurveyService'
import UserService from '@/services/users/UserService'

export default {
  name: 'SurveyManagement',
  data() {
    return {
      searchTerm: '',
      entriesPerPage: 10,
      currentPage: 1,
      loading: false,
      creating: false,
      editing: false,
      previewLoading: false,
      closing: false,
      sending: false,
      deleting: false,
      restoring: false,
      managing: false,
      loadingResults: false,
      loadingManagement: false,
      loadingIndividualAnswers: false,

      // Modal states
      showCreateModal: false,
      showEditModal: false,
      showPreviewModal: false,
      showCloseSurveyModal: false,
      showDeleteModal: false,
      showRestoreModal: false,
      showResultsModal: false,
      showManageModal: false,
      showIndividualAnswersModal: false,

      // Modal data
      surveyToClose: null,
      surveyToDelete: null,
      surveyToRestore: null,
      selectedSurvey: null,
      editingSurvey: null,
      previewSurveyData: null,
      selectedResult: null,
      individualAnswers: null,

      // Survey forms
      newSurvey: {
        title: '',
        description: '',
        questions: []
      },
      editSurvey: {
        title: '',
        description: '',
        questions: []
      },

      // Data
      surveys: [],
      surveyResults: null,
      managementData: null,
      availableUsers: [],
      invitedUsers: [],

      // Search filters for manage modal
      availableUsersSearch: '',
      invitedUsersSearch: '',

      // Results pagination
      resultsPerPage: 10,
      resultsCurrentPage: 1,
      resultsSearchTerm: '',

      toast: {
        show: false,
        message: '',
        type: 'success'
      }
    }
  },
  computed: {
    filteredSurveys() {
      if (!this.searchTerm) return this.surveys
      return this.surveys.filter(survey =>
        survey.title.toLowerCase().includes(this.searchTerm.toLowerCase())
      )
    },
    totalPages() {
      return Math.ceil(this.filteredSurveys.length / this.entriesPerPage)
    },
    paginatedSurveys() {
      const start = (this.currentPage - 1) * this.entriesPerPage
      const end = start + parseInt(this.entriesPerPage)
      return this.filteredSurveys.slice(start, end)
    },
    startEntry() {
      return (this.currentPage - 1) * this.entriesPerPage + 1
    },
    endEntry() {
      const end = this.currentPage * this.entriesPerPage
      return Math.min(end, this.filteredSurveys.length)
    },

    // Manage modal computed properties
    filteredAvailableUsers() {
      if (!this.availableUsersSearch) return this.availableUsers
      return this.availableUsers.filter(user =>
        user.name.toLowerCase().includes(this.availableUsersSearch.toLowerCase()) ||
        user.email.toLowerCase().includes(this.availableUsersSearch.toLowerCase())
      )
    },
    filteredInvitedUsers() {
      if (!this.invitedUsersSearch) return this.invitedUsers
      return this.invitedUsers.filter(user =>
        user.name.toLowerCase().includes(this.invitedUsersSearch.toLowerCase()) ||
        user.email.toLowerCase().includes(this.invitedUsersSearch.toLowerCase())
      )
    },
    hasSelectedAvailable() {
      return this.availableUsers.some(user => user.selected)
    },
    hasSelectedInvited() {
      return this.invitedUsers.some(user => user.selected)
    },

    // Results computed properties
    filteredResults() {
      if (!this.surveyResults || !this.surveyResults.responses) return []
      if (!this.resultsSearchTerm) return this.surveyResults.responses
      return this.surveyResults.responses.filter(result =>
        result.user.name.toLowerCase().includes(this.resultsSearchTerm.toLowerCase()) ||
        result.user.email.toLowerCase().includes(this.resultsSearchTerm.toLowerCase())
      )
    },
    resultsTotalPages() {
      return Math.ceil(this.filteredResults.length / this.resultsPerPage)
    },
    paginatedResults() {
      const start = (this.resultsCurrentPage - 1) * this.resultsPerPage
      const end = start + parseInt(this.resultsPerPage)
      return this.filteredResults.slice(start, end)
    },
    resultsStartEntry() {
      return (this.resultsCurrentPage - 1) * this.resultsPerPage + 1
    },
    resultsEndEntry() {
      const end = this.resultsCurrentPage * this.resultsPerPage
      return Math.min(end, this.filteredResults.length)
    }
  },
  async mounted() {
    await this.loadSurveys()
  },
  methods: {
    showToast(type, message) {
      this.toast = {
        show: true,
        type: type,
        message: message
      }
      setTimeout(() => {
        this.toast.show = false
      }, 3000)
    },

    goBackToSurveys() {
      this.showManageModal = false
      this.showResultsModal = false
    },

    // Load surveys from API
    async loadSurveys() {
      try {
        this.loading = true
        const response = await SurveyService.getSurveys()

        let surveysData = null
        // Handle different response formats
        if (response && response.Data && response.Data.data) {
          surveysData = response.Data.data
        } else if (response && response.data) {
          surveysData = response.data
        } else if (response && Array.isArray(response)) {
          surveysData = response
        } else {
          console.error('❌ Unexpected response format:', response)
          this.surveys = []
          this.showToast('error', 'Failed to load surveys - unexpected response format')
          return
        }

        // Validate that we got surveys, not users
        if (surveysData && surveysData.length > 0) {
          const firstItem = surveysData[0]
          // Check if this looks like user data instead of survey data
          if (firstItem.email && firstItem.name && !firstItem.title) {
            console.error('❌ ERROR: API returned USERS instead of SURVEYS!')
            console.error('Check your API endpoint - /api/v1/surveys should return surveys, not users')
            this.showToast('error', 'API Error: Surveys endpoint returning user data')
            this.surveys = []
            return
          }
          // Check if it has survey properties
          if (!firstItem.title && !firstItem.structure && !firstItem.description) {
            console.warn('⚠️ WARNING: Data doesn\'t look like survey objects')
            console.warn('Expected properties: title, description, structure')
            console.warn('Got:', Object.keys(firstItem))
          }
        }

        this.surveys = surveysData || []
      } catch (error) {
        console.error('❌ Error loading surveys:', error)
        this.surveys = []
        this.showToast('error', 'Error loading surveys')
      } finally {
        this.loading = false
      }
    },

    // Enhanced edit survey modal
    editSurveyModal(survey) {
      this.editingSurvey = survey
      this.editSurvey = {
        title: survey.title,
        description: survey.description,
        questions: survey.structure?.questions ? [...survey.structure.questions].sort((a, b) => ((a.order || 0) - (b.order || 0))).map(q => ({
          id: q.id,
          text: q.text,
          type: q.type,
          required: q.required ? 'yes' : 'no',
          options: q.options || [],
          order: q.order || 1,
          placeholder: q.placeholder || '',
          allowOthers: q.allowOthers ? 'yes' : 'no',
          scaleMin: q.scaleMin || 1,
          scaleMax: q.scaleMax || 5,
          scaleMinLabel: q.scaleMinLabel || '',
          scaleMaxLabel: q.scaleMaxLabel || '',
          charLimit: q.charLimit || null
        })) : []
      }
      this.showEditModal = true
    },

    // Enhanced preview survey modal
    async previewSurveyModal(survey) {
      try {
        this.previewLoading = true
        this.showPreviewModal = true
        const response = await SurveyService.previewSurvey(survey.id)
        if (response && (response.Data?.success || response.success)) {
          const data = response.Data?.data || response.data
          this.previewSurveyData = {
            title: data.title,
            description: data.description,
            questions: data.questions || data.structure?.questions || []
          }
        } else {
          this.showToast('error', 'Failed to load survey preview')
        }
      } catch (error) {
        console.error('Error loading survey preview:', error)
        this.showToast('error', 'Error loading survey preview')
      } finally {
        this.previewLoading = false
      }
    },

    // Enhanced manage survey modal
    async manageSurvey(survey) {
      try {
        this.selectedSurvey = survey
        this.loadingManagement = true
        this.showManageModal = true
        // Reset data
        this.availableUsers = []
        this.invitedUsers = []
        this.availableUsersSearch = ''
        this.invitedUsersSearch = ''
        // Load users data
        await this.loadManagementData(survey.id)
      } catch (error) {
        console.error('Error in manageSurvey:', error)
        this.showToast('error', 'Error loading management data')
        this.showManageModal = false
      } finally {
        this.loadingManagement = false
      }
    },

    // Load management data (users available and invited)
    async loadManagementData(surveyId) {
      try {
        // Get users
        const usersResponse = await UserService.getUsers()
        // Get management data
        const managementResponse = await SurveyService.getManagementData(surveyId)

        // Extract users data
        let allUsers = []
        if (usersResponse && usersResponse.data) {
          allUsers = usersResponse.data
        } else if (usersResponse && usersResponse.Data && usersResponse.Data.data) {
          allUsers = usersResponse.Data.data
        }

        // Extract management data
        let managementData = {}
        if (managementResponse && managementResponse.data) {
          managementData = managementResponse.data
        } else if (managementResponse && managementResponse.Data && managementResponse.Data.data) {
          managementData = managementResponse.Data.data
        }

        const invitedUsersData = managementData.invited_users || []
        const invitedUserIds = invitedUsersData.map(user => user.id)

        // Separate users
        this.invitedUsers = allUsers
          .filter(user => invitedUserIds.includes(user.id))
          .map(user => ({ ...user, selected: false }))

        this.availableUsers = allUsers
          .filter(user => !invitedUserIds.includes(user.id))
          .map(user => ({ ...user, selected: false }))

      } catch (error) {
        console.error('Error loading management data:', error)
        this.showToast('error', 'Error loading management data')
      }
    },

    // Send survey invitations
    async sendSurveyInvitations() {
      if (this.invitedUsers.length === 0) {
        this.showToast('error', 'No users invited to this survey')
        return
      }

      try {
        this.sending = true
        const userIds = this.invitedUsers.map(user => user.id)
        const response = await SurveyService.sendSurveyInvitations(this.selectedSurvey.id, userIds)

        let success = false
        let message = ''
        let surveyActivated = false

        if (response?.success || response?.Data?.success) {
          success = true
          message = response?.message || response?.Data?.message || 'Survey invitations sent successfully'
          surveyActivated = response?.data?.survey_activated || response?.Data?.data?.survey_activated || false
        }

        if (success) {
          let successMessage = message
          if (surveyActivated) {
            successMessage += ' Survey has been automatically activated.'
          }
          this.showToast('success', successMessage)
          // Reload surveys to reflect status change
          await this.loadSurveys()
        } else {
          this.showToast('error', message || 'Failed to send invitations')
        }
      } catch (error) {
        console.error('Error sending survey invitations:', error)
        this.showToast('error', 'Error sending survey invitations')
      } finally {
        this.sending = false
      }
    },

    // Enhanced checkResults method
    async checkResults(survey) {
      try {
        this.selectedSurvey = survey
        this.loadingResults = true
        this.showResultsModal = true
        this.resultsCurrentPage = 1 // Reset pagination

        const response = await SurveyService.getSurveyResults(survey.id)

        // Handle different response formats
        let success = false
        let data = null

        if (response && response.Data && response.Data.success) {
          // ServiceResponse format: { Data: { success: true, data: {...} }, Success: true }
          success = response.Data.success
          data = response.Data.data
        } else if (response && response.success !== undefined) {
          // Direct format: { success: true, data: {...} }
          success = response.success
          data = response.data
        }

        if (success && data) {
          this.surveyResults = data

          // Validate the structure
          if (!this.surveyResults.responses || !Array.isArray(this.surveyResults.responses)) {
            console.warn('Survey results missing responses array')
            this.surveyResults.responses = []
          }

        } else {
          console.error('Failed to load survey results:', response)
          this.showToast('error', 'Failed to load survey results')
          this.surveyResults = { responses: [], questions: [] }
        }
      } catch (error) {
        console.error('Error loading survey results:', error)
        this.showToast('error', 'Error loading survey results')
        this.surveyResults = { responses: [], questions: [] }
      } finally {
        this.loadingResults = false
      }
    },

    // New method to view individual user answers
    async viewIndividualAnswers(result) {
      try {
        this.selectedResult = result
        this.loadingIndividualAnswers = true
        this.showIndividualAnswersModal = true

        // Get the survey questions from the current survey
        let surveyQuestions = []
        if (this.selectedSurvey && this.selectedSurvey.structure && this.selectedSurvey.structure.questions) {
          surveyQuestions = this.selectedSurvey.structure.questions
        }

        // Parse answers from multiple possible sources
        let parsedAnswers = {}

        // Method 1: From result.answers (JSON structure)
        if (result.answers) {
          if (typeof result.answers === 'string') {
            try {
              parsedAnswers = JSON.parse(result.answers)
            } catch (e) {
              console.error('Failed to parse answers JSON:', e)
            }
          } else if (typeof result.answers === 'object') {
            parsedAnswers = result.answers
          }
        }

        // Method 2: From result.structure (if available)
        if (result.structure) {
          if (typeof result.structure === 'string') {
            try {
              const structureData = JSON.parse(result.structure)
              parsedAnswers = { ...parsedAnswers, ...structureData }
            } catch (e) {
              console.error('Failed to parse structure JSON:', e)
            }
          } else if (typeof result.structure === 'object') {
            parsedAnswers = { ...parsedAnswers, ...result.structure }
          }
        }

        // Method 3: From survey_answer_elements (if available in the response)
        const answerElements = result.answer_elements || result.elements || []

        // Create a map from answer elements
        const elementMap = {}
        answerElements.forEach(element => {
          elementMap[element.question_key] = {
            answer_text: element.answer_text,
            answer_key: element.answer_key,
            order: element.order
          }
        })

        // Format the individual answers for display
        this.individualAnswers = {
          user: result.user,
          submitted_at: result.submitted_at,
          questions: surveyQuestions.map(question => {

            let userAnswer = null

            // Try to find answer by question ID
            if (parsedAnswers[question.id]) {
              userAnswer = parsedAnswers[question.id]
            }
            // Try to find answer in element map
            else if (elementMap[question.id]) {
              userAnswer = elementMap[question.id]
            }
            // Try alternative matching by question text or order
            else {
              // Look for answers by matching question order or text
              answerElements.forEach(element => {
                if (!userAnswer) {
                  // Try matching by order
                  if (element.order === question.order || element.order === (question.order + '')) {
                    userAnswer = {
                      answer_text: element.answer_text,
                      answer_key: element.answer_key
                    }
                  }
                }
              })
            }

            return {
              id: question.id,
              text: question.text,
              type: question.type,
              options: question.options || [],
              required: question.required,
              answer: userAnswer ? {
                answer_text: userAnswer.answer_text || userAnswer.answer_key || '',
                answer_key: userAnswer.answer_key || userAnswer.answer_text || ''
              } : null
            }
          })
        }

        // Debug: Check if we found any answers
        const answeredQuestions = this.individualAnswers.questions.filter(q => q.answer && q.answer.answer_text)

      } catch (error) {
        console.error('Error loading individual answers:', error)
        this.showToast('error', 'Error loading individual answers')
      } finally {
        this.loadingIndividualAnswers = false
      }
    },

    // Close individual answers modal
    closeIndividualAnswersModal() {
      this.showIndividualAnswersModal = false
      this.selectedResult = null
      this.individualAnswers = null
    },

    // Enhanced export results method
    async exportResults() {
      if (!this.surveyResults || !this.surveyResults.responses) {
        this.showToast('error', 'No results to export')
        return
      }

      try {
        // Create CSV content
        let csvContent = "data:text/csv;charset=utf-8,"

        // Headers
        const headers = ['Participant Name', 'Email', 'Submitted Date']

        // Add question headers
        if (this.surveyResults.questions) {
          this.surveyResults.questions.forEach(question => {
            headers.push(`Q: ${question.text}`)
          })
        }

        csvContent += headers.join(',') + '\n'

        // Data rows
        this.surveyResults.responses.forEach(response => {
          const row = [
            `"${response.user.name}"`,
            `"${response.user.email}"`,
            `"${this.formatDateTime(response.submitted_at)}"`
          ]

          // Add answers
          if (this.surveyResults.questions) {
            this.surveyResults.questions.forEach(question => {
              const answer = response.answers ? response.answers[question.id] : null
              const answerText = answer ? answer.answer_text || answer.answer_key || '' : ''
              row.push(`"${answerText.replace(/"/g, '""')}"`) // Escape quotes
            })
          }

          csvContent += row.join(',') + '\n'
        })

        // Download the file
        const encodedUri = encodeURI(csvContent)
        const link = document.createElement('a')
        link.setAttribute('href', encodedUri)
        link.setAttribute('download', `survey-results-${this.selectedSurvey.title}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        this.showToast('success', 'Results exported successfully!')

      } catch (error) {
        console.error('Error exporting results:', error)
        this.showToast('error', 'Error exporting results')
      }
    },

    // Helper method to get question type label for display
    getQuestionTypeLabel(type) {
      const typeLabels = {
        'single_line_text': 'Single Line Text',
        'multiple_line_text': 'Multiple Line Text',
        'text': 'Text Input',
        'textarea': 'Text Area',
        'multiple_choice_single': 'Multiple Choice (Single)',
        'multiple_choice_multiple': 'Multiple Choice (Multiple)',
      }
      return typeLabels[type] || type
    },

    // Helper method to parse selected options for multiple choice questions
    getSelectedOptions(answerText) {
      if (!answerText) return []

      // Handle comma-separated values
      if (answerText.includes(',')) {
        return answerText.split(',').map(option => option.trim()).filter(option => option)
      }

      // Single option
      return [answerText.trim()]
    },

    // Helper method to format date for display
    formatDate(dateString) {
      if (!dateString) return 'Invalid date'

      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      } catch (error) {
        return dateString // Return original if parsing fails
      }
    },

    // Helper method to count answered questions
    getAnsweredCount() {
      if (!this.individualAnswers || !this.individualAnswers.questions) return 0

      return this.individualAnswers.questions.filter(question => {
        return question.answer && question.answer.answer_text && question.answer.answer_text.trim() !== ''
      }).length
    },

    // Helper method to count unanswered questions
    getUnansweredCount() {
      if (!this.individualAnswers || !this.individualAnswers.questions) return 0

      return this.individualAnswers.questions.filter(question => {
        return !question.answer || !question.answer.answer_text || question.answer.answer_text.trim() === ''
      }).length
    },

    // Helper method to calculate completion percentage
    getCompletionPercentage() {
      if (!this.individualAnswers || !this.individualAnswers.questions || this.individualAnswers.questions.length === 0) {
        return 0
      }

      const answered = this.getAnsweredCount()
      const total = this.individualAnswers.questions.length

      return (answered / total) * 100
    },

    // Survey actions
    closeSurvey(survey) {
      this.surveyToClose = survey
      this.showCloseSurveyModal = true
    },
    async deleteSurvey(survey) {
      this.surveyToDelete = survey
      this.showDeleteModal = true
      await this.loadSurveys()
    },
    async restoreSurvey(survey) {
      this.surveyToRestore = survey
      this.showRestoreModal = true
      await this.loadSurveys()
    },

    // Confirmation methods
    async confirmCloseSurvey() {
      if (!this.surveyToClose) return
      try {
        this.closing = true
        const response = await SurveyService.closeSurvey(this.surveyToClose.id)
        console.log('Survey response: ', response)
        if (response.Data.success) {
          this.showToast('success', 'Survey closed successfully!')
          await this.loadSurveys()
        } else {
          this.showToast('error', response.message || 'Failed to close survey')
        }
      } catch (error) {
        console.error('Error closing survey:', error)
        this.showToast('error', 'Error closing survey')
      } finally {
        this.closing = false
        this.showCloseSurveyModal = false
        this.surveyToClose = null
      }
    },
    async confirmDeleteSurvey() {
      if (!this.surveyToDelete) return
      try {
        this.deleting = true
        const response = await SurveyService.deleteSurvey(this.surveyToDelete.id)
        if (response.Data.success) {
          this.showToast('success', 'Survey deleted successfully!')
          await this.loadSurveys()
        } else {
          this.showToast('error', response.message || 'Failed to delete survey')
        }
      } catch (error) {
        console.error('Error deleting survey:', error)
        this.showToast('error', 'Error deleting survey')
      } finally {
        this.deleting = false
        this.showDeleteModal = false
        this.surveyToDelete = null
      }
    },
    async confirmRestoreSurvey() {
      if (!this.surveyToRestore) return
      try {
        this.restoring = true
        const response = await SurveyService.restoreSurvey(this.surveyToRestore.id)
        if (response.Data.success) {
          this.showToast('success', 'Survey restored successfully!')
          await this.loadSurveys()
        } else {
          this.showToast('error', response.message || 'Failed to restore survey')
        }
      } catch (error) {
        console.error('Error restoring survey:', error)
        this.showToast('error', 'Error restoring survey')
      } finally {
        this.restoring = false
        this.showRestoreModal = false
        this.surveyToRestore = null
      }
    },

    // Create survey methods
    async createSurvey() {
      if (!this.newSurvey.title?.trim()) {
        this.showToast('error', 'Please enter a survey title')
        return
      }
      try {
        this.creating = true
        const formattedQuestions = this.newSurvey.questions.map((question, index) => ({
          id: question.id,
          text: question.text?.trim() || '',
          type: question.type,
          required: question.required === 'yes',
          options: Array.isArray(question.options) ? question.options.filter(opt => opt?.trim()) : [],
          order: question.order || (index + 1),
          placeholder: question.placeholder?.trim() || '',
          allowOthers: question.allowOthers === 'yes',
          scaleMin: parseInt(question.scaleMin) || 1,
          scaleMax: parseInt(question.scaleMax) || 5,
          scaleMinLabel: question.scaleMinLabel?.trim() || '',
          scaleMaxLabel: question.scaleMaxLabel?.trim() || '',
          charLimit: question.charLimit ? parseInt(question.charLimit) : null
        }))

        const surveyData = {
          title: this.newSurvey.title.trim(),
          description: this.newSurvey.description?.trim() || '',
          structure: {
            questions: formattedQuestions
          },
          status: 1 // Draft
        }

        const response = await SurveyService.createSurvey(surveyData)

        // Handle multiple response formats
        let success = false
        let data = null

        if (response && response.Data && response.Data.success) {
          success = true
          data = response.Data.data
        } else if (response && response.data && response.data.success) {
          success = true
          data = response.data.data
        } else if (response && response.success) {
          success = true
          data = response.data
        } else if (response && response.Data && response.Data.data) {
          success = true
          data = response.Data.data
        }

        if (success) {
          this.showToast('success', 'Survey created successfully!')
          this.resetSurveyForm()
          this.showCreateModal = false
          await this.loadSurveys()
        } else {
          const errorMessage = response?.message || response?.Data?.message || 'Failed to create survey'
          this.showToast('error', errorMessage)
        }
      } catch (error) {
        console.error('Error creating survey:', error)
        const errorMessage = error.response?.data?.message || error.message || 'Error creating survey'
        this.showToast('error', errorMessage)
      } finally {
        this.creating = false
      }
    },

    // Update survey methods with validation
    async updateSurvey() {
      if (!this.editSurvey.title?.trim()) {
        this.showToast('error', 'Please enter a survey title')
        return
      }
      try {
        this.editing = true
        const formattedQuestions = this.editSurvey.questions.map((question, index) => ({
          id: question.id,
          text: question.text?.trim() || '',
          type: question.type,
          required: question.required === 'yes',
          options: Array.isArray(question.options) ? question.options.filter(opt => opt?.trim()) : [],
          order: question.order || (index + 1),
          placeholder: question.placeholder?.trim() || '',
          allowOthers: question.allowOthers === 'yes',
          scaleMin: parseInt(question.scaleMin) || 1,
          scaleMax: parseInt(question.scaleMax) || 5,
          scaleMinLabel: question.scaleMinLabel?.trim() || '',
          scaleMaxLabel: question.scaleMaxLabel?.trim() || '',
          charLimit: question.charLimit ? parseInt(question.charLimit) : null
        }))

        const surveyData = {
          title: this.editSurvey.title.trim(),
          description: this.editSurvey.description?.trim() || '',
          structure: {
            questions: formattedQuestions
          }
        }

        const response = await SurveyService.updateSurvey(this.editingSurvey.id, surveyData)
        if (response && (response.Data?.success || response.success)) {
          this.showToast('success', 'Survey updated successfully!')
          this.cancelEditSurvey()
          await this.loadSurveys()
        } else {
          console.error('Failed to update survey:', response)
          const errorMessage = response?.message || response?.Data?.message || 'Failed to update survey'
          this.showToast('error', errorMessage)
        }
      } catch (error) {
        console.error('Error updating survey:', error)
        const errorMessage = error.response?.data?.message || error.message || 'Error updating survey'
        this.showToast('error', errorMessage)
      } finally {
        this.editing = false
      }
    },

    // Validation method
    validateSurveyData() {
      const errors = []
      if (!this.editSurvey.title?.trim()) {
        errors.push('Survey title is required')
      }
      if (this.editSurvey.questions.length === 0) {
        errors.push('At least one question is required')
      }
      this.editSurvey.questions.forEach((question, index) => {
        if (!question.text?.trim()) {
          errors.push(`Question ${index + 1}: Question text is required`)
        }
        if (!question.type) {
          errors.push(`Question ${index + 1}: Question type is required`)
        }
        if ((question.type === 'multiple_choice_single' ||
            question.type === 'multiple_choice_multiple' ||
            question.type === 'dropdown') &&
          (!question.options || question.options.length < 2)) {
          errors.push(`Question ${index + 1}: At least 2 options are required`)
        }
        if (question.type === 'rating_scale') {
          if (question.scaleMin >= question.scaleMax) {
            errors.push(`Question ${index + 1}: Scale minimum must be less than maximum`)
          }
        }
      })
      return errors
    },
    async updateSurveyWithValidation() {
      const validationErrors = this.validateSurveyData()
      if (validationErrors.length > 0) {
        this.showToast('error', validationErrors[0])
        return
      }
      await this.updateSurvey()
    },

    // Survey form management
    resetSurveyForm() {
      this.newSurvey = {
        title: '',
        description: '',
        questions: []
      }
    },
    cancelSurvey() {
      this.resetSurveyForm()
      this.showCreateModal = false
    },
    cancelEditSurvey() {
      this.showEditModal = false
      this.editingSurvey = null
      this.editSurvey = {
        title: '',
        description: '',
        questions: []
      }
    },
    saveSurvey() {
      this.createSurvey()
    },

    // Question management for create modal
    addNewQuestion() {
      // Use a more consistent ID format
      const questionId = `question_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

      const newQuestion = {
        id: questionId,
        text: '',
        type: '',
        required: 'no',
        order: this.newSurvey.questions.length + 1,
        options: [],
        allowOthers: 'no',
        scaleMin: 1,
        scaleMax: 5,
        scaleMinLabel: '',
        scaleMaxLabel: '',
        placeholder: '',
        charLimit: null
      }
      this.newSurvey.questions.push(newQuestion)
    },

    removeQuestion(index) {
      if (index >= 0 && index < this.newSurvey.questions.length) {
        this.newSurvey.questions.splice(index, 1)
        this.newSurvey.questions.forEach((question, i) => {
          question.order = i + 1
        })
      }
    },
    onQuestionTypeChange(question) {
      if (!question) return
      question.options = []
      question.allowOthers = 'no'
      if (question.type === 'multiple_choice_single' ||
        question.type === 'multiple_choice_multiple' ||
        question.type === 'dropdown') {
        question.options = ['Option 1', 'Option 2']
      }
      if (question.type === 'rating_scale') {
        question.scaleMin = 1
        question.scaleMax = 5
        question.scaleMinLabel = ''
        question.scaleMaxLabel = ''
      }
      if (question.type !== 'single_line_text' && question.type !== 'multiple_line_text') {
        question.placeholder = ''
        question.charLimit = null
      }
    },
    addOption(question) {
      if (!question || !Array.isArray(question.options)) {
        question.options = []
      }
      question.options.push(`Option ${question.options.length + 1}`)
    },
    removeOption(question, optionIndex) {
      if (!question || !Array.isArray(question.options)) return
      if (optionIndex >= 0 && optionIndex < question.options.length) {
        question.options.splice(optionIndex, 1)
      }
    },

    // Question management for edit modal
    addNewEditQuestion() {
      const questionId = `question_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

      const newQuestion = {
        id: questionId,
        text: '',
        type: '',
        required: 'no',
        order: this.editSurvey.questions.length + 1,
        options: [],
        allowOthers: 'no',
        scaleMin: 1,
        scaleMax: 5,
        scaleMinLabel: '',
        scaleMaxLabel: '',
        placeholder: '',
        charLimit: null
      }
      this.editSurvey.questions.push(newQuestion)
    },

    removeEditQuestion(index) {
      if (index >= 0 && index < this.editSurvey.questions.length) {
        this.editSurvey.questions.splice(index, 1)
        this.editSurvey.questions.forEach((question, i) => {
          question.order = i + 1
        })
      }
    },
    onEditQuestionTypeChange(question) {
      if (!question) return
      question.options = []
      question.allowOthers = 'no'
      if (question.type === 'multiple_choice_single' ||
        question.type === 'multiple_choice_multiple' ||
        question.type === 'dropdown') {
        question.options = ['Option 1', 'Option 2']
      }
      if (question.type === 'rating_scale') {
        question.scaleMin = 1
        question.scaleMax = 5
        question.scaleMinLabel = ''
        question.scaleMaxLabel = ''
      }
      if (question.type !== 'single_line_text' && question.type !== 'multiple_line_text') {
        question.placeholder = ''
        question.charLimit = null
      }
    },
    addEditOption(question) {
      if (!question || !Array.isArray(question.options)) {
        question.options = []
      }
      question.options.push(`Option ${question.options.length + 1}`)
    },
    removeEditOption(question, optionIndex) {
      if (!question || !Array.isArray(question.options)) return
      if (optionIndex >= 0 && optionIndex < question.options.length) {
        question.options.splice(optionIndex, 1)
      }
    },

    // User management methods for manage modal
    selectAllAvailable() {
      this.availableUsers.forEach(user => user.selected = true)
    },
    unselectAllAvailable() {
      this.availableUsers.forEach(user => user.selected = false)
    },
    selectAllInvited() {
      this.invitedUsers.forEach(user => user.selected = true)
    },
    unselectAllInvited() {
      this.invitedUsers.forEach(user => user.selected = false)
    },
    getSelectedCount(type) {
      if (type === 'available') {
        return this.availableUsers.filter(user => user.selected).length
      } else if (type === 'invited') {
        return this.invitedUsers.filter(user => user.selected).length
      }
      return 0
    },
    async inviteSelected() {
      const selectedUsers = this.availableUsers.filter(user => user.selected)
      if (selectedUsers.length === 0) return
      try {
        this.managing = true
        const userIds = selectedUsers.map(user => user.id)
        const response = await SurveyService.manageInvitations(this.selectedSurvey.id, {
          action: 'invite',
          user_ids: userIds
        })
        // Check if successful
        let success = false
        let message = ''
        let surveyActivated = false
        if (response?.success || response?.Data?.success) {
          success = true
          message = response?.message || response?.Data?.message || 'Users invited successfully'
          surveyActivated = response?.data?.survey_activated || response?.Data?.data?.survey_activated || false
        }
        if (success) {
          let successMessage = `${selectedUsers.length} users invited successfully!`
          if (surveyActivated) {
            successMessage += ' Survey has been automatically activated.'
          }
          this.showToast('success', successMessage)
          // Move users
          selectedUsers.forEach(user => {
            user.selected = false
            this.invitedUsers.push({...user})
          })
          this.availableUsers = this.availableUsers.filter(user => !selectedUsers.includes(user))
          // Reload surveys to reflect status change
          await this.loadSurveys()
        } else {
          this.showToast('error', message || 'Failed to invite users')
        }
      } catch (error) {
        console.error('Error inviting users:', error)
        this.showToast('error', 'Error inviting users')
      } finally {
        this.managing = false
      }
    },
    async uninviteSelected() {
      const selectedUsers = this.invitedUsers.filter(user => user.selected)
      if (selectedUsers.length === 0) return
      try {
        this.managing = true
        const userIds = selectedUsers.map(user => user.id)
        const response = await SurveyService.manageInvitations(this.selectedSurvey.id, {
          action: 'uninvite',
          user_ids: userIds
        })
        // Handle different response formats
        let success = false
        let message = ''
        if (response && response.success) {
          success = response.success
          message = response.message
        } else if (response && response.Data && response.Data.success) {
          success = response.Data.success
          message = response.Data.message
        }
        if (success) {
          this.showToast('success', `${selectedUsers.length} users removed successfully!`)
          // Move selected users from invited to available
          selectedUsers.forEach(user => {
            user.selected = false
            this.availableUsers.push({...user})
          })
          this.invitedUsers = this.invitedUsers.filter(user => !selectedUsers.includes(user))
        } else {
          this.showToast('error', message || 'Failed to remove users')
        }
      } catch (error) {
        console.error('Error removing users:', error)
        this.showToast('error', 'Error removing users')
      } finally {
        this.managing = false
      }
    },

    // Utility methods
    getStatusBadgeClass(status) {
      const classes = {
        'Active': 'bg-success',
        'Closed': 'bg-info',
        'Inactive': 'bg-danger',
        'Draft': 'bg-secondary'
      }
      return classes[status] || 'bg-secondary'
    },
    formatDateTime(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleString()
    }
  }
}
</script>

<style scoped>
.survey-management-page {
  padding: 1rem 0;
}
.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
}

/* Table Styles */
.table th {
  font-weight: 600;
  color: #6c757d;
  border-bottom: 2px solid #dee2e6;
}
.table td {
  vertical-align: middle;
}

/* Badge Styles */
.badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
}
.bg-info { background-color: #0dcaf0 !important; }
.bg-success { background-color: #198754 !important; }
.bg-danger { background-color: #dc3545 !important; }
.bg-secondary { background-color: #6c757d !important; }

/* Pagination Styles */
.pagination-sm .page-link {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}
.page-item.active .page-link {
  background-color: #009CAD;
  border-color: #009CAD;
}

/* Modal Styles */
.modal-dialog { max-width: 500px; }
.modal-xl { max-width: 1200px; }
.modal-lg { max-width: 800px; }
.modal-header {
  border-bottom: 1px solid #dee2e6;
  padding: 1rem 1.5rem;
}
.modal-body { padding: 1.5rem; }
.modal-footer {
  border-top: 1px solid #dee2e6;
  padding: 1rem 1.5rem;
}

/* Survey Builder Styles */
.survey-structure {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 0.5rem;
  margin-top: 1rem;
}
.question-block {
  background-color: white;
  padding: 1.5rem;
  border-radius: 0.5rem;
  border: 1px solid #dee2e6;
}

/* Survey Preview Styles */
.survey-preview-container {
  max-height: 70vh;
  overflow-y: auto;
}
.survey-header {
  border-left: 4px solid #009CAD;
}
.survey-title {
  color: #2c3e50;
  font-weight: 600;
}
.question-preview {
  transition: all 0.2s ease;
}
.question-preview:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}
.question-label {
  color: #495057;
  font-size: 1.1rem;
  line-height: 1.5;
}

/* Rating Scale Styles */
.rating-scale-container {
  padding: 1rem 0;
}
.rating-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}
.rating-option:hover {
  background-color: #f8f9fa;
}
.rating-input {
  margin-bottom: 0.25rem;
}
.rating-label {
  font-weight: 500;
  color: #495057;
}

/* User Management Styles */
.user-list-container {
  height: 100%;
}
.user-list {
  background-color: #f8f9fa;
}
.user-item {
  padding: 0.75rem;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
  background-color: white;
  border: 1px solid #e9ecef;
}
.user-item:hover {
  background-color: #f8f9fa;
  border-color: #dee2e6;
}
.user-label {
  cursor: pointer;
  margin-bottom: 0;
  width: 100%;
}
.user-name {
  font-weight: 500;
  color: #495057;
}
.user-email {
  font-size: 0.875rem;
  color: #6c757d;
}
.stat-item {
  padding: 0.5rem;
}
.stat-number {
  font-size: 1.5rem;
  font-weight: 600;
}
.stat-label {
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Individual Answers Modal Styles */
.user-info-header {
  border-left: 4px solid #009CAD;
}
.survey-info-section {
  border-left: 3px solid #20c997;
}
.answers-container {
  max-height: 60vh;
  overflow-y: auto;
}
.answer-item {
  transition: all 0.2s ease;
  border-left: 3px solid #e9ecef;
}
.answer-item:hover {
  border-left-color: #009CAD;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}
.question-number {
  min-width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
}
.question-text {
  color: #495057;
  font-weight: 600;
  line-height: 1.4;
}
.question-type {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.answer-content {
  min-height: 2.5rem;
  display: flex;
  align-items: center;
}
.answer-text {
  color: #212529;
  font-weight: 500;
  line-height: 1.5;
}
.selected-option {
  display: flex;
  align-items: center;
  margin-bottom: 0.25rem;
}
.selected-option-highlight {
  background-color: #e7f3ff;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-weight: 500;
}
.options-list {
  max-height: 150px;
  overflow-y: auto;
  background-color: #f8f9fa;
  border-radius: 0.25rem;
  padding: 0.5rem;
}
.option-item {
  display: flex;
  align-items: center;
  padding: 0.25rem 0;
  font-size: 0.875rem;
}
.rating-display {
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 0.375rem;
  border: 1px solid #dee2e6;
}
.rating-value {
  font-size: 1.1rem;
  padding: 0.5rem 0.75rem;
}
.rating-stars i {
  font-size: 1.2rem;
  margin-right: 0.1rem;
}
.yes-no-answer {
  display: flex;
  align-items: center;
  font-size: 1.1rem;
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 0.375rem;
  border: 1px solid #dee2e6;
}
.date-answer,
.email-answer,
.number-answer {
  display: flex;
  align-items: center;
  font-size: 1rem;
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 0.375rem;
  border: 1px solid #dee2e6;
}
.no-answer {
  display: flex;
  align-items: center;
  font-style: italic;
  color: #6c757d;
  padding: 1rem;
}
.response-summary {
  border-left: 4px solid #0dcaf0;
}
.summary-stat {
  padding: 0.5rem;
}

/* Alert Styles */
.alert {
  border-radius: 0.375rem;
  margin-bottom: 1rem;
}
.alert-warning {
  color: #664d03;
  background-color: #fff3cd;
  border-color: #ffecb5;
}
.alert-danger {
  color: #721c24;
  background-color: #f8d7da;
  border-color: #f5c2c7;
}
.alert-success {
  color: #0f5132;
  background-color: #d1e7dd;
  border-color: #badbcc;
}

/* Form Styles */
.form-check-input:checked {
  background-color: #009CAD;
  border-color: #009CAD;
}
.form-control-lg {
  font-size: 1.1rem;
  padding: 0.75rem;
}

/* Button Styles */
.btn-warning {
  background-color: #ffc107;
  border-color: #ffc107;
  color: #000;
}
.btn-warning:hover {
  background-color: #e0a800;
  border-color: #d39e00;
  color: #000;
}
.btn-info {
  background-color: #0dcaf0;
  border-color: #0dcaf0;
}
.btn-info:hover {
  background-color: #31d2f2;
  border-color: #25cff2;
}
.btn:disabled {
  opacity: 0.65;
  pointer-events: none;
}

/* Utility Classes */
.text-danger { color: #dc3545 !important; }
.text-success { color: #198754 !important; }
.text-primary { color: #009CAD !important; }
.text-muted { color: #6c757d !important; }
.spinner-border {
  color: #009CAD;
}

/* Animation for modal transitions */
.modal.show {
  animation: fadeIn 0.3s ease-in-out;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Scrollbar styling for better UX */
.answers-container::-webkit-scrollbar,
.options-list::-webkit-scrollbar {
  width: 6px;
}
.answers-container::-webkit-scrollbar-track,
.options-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}
.answers-container::-webkit-scrollbar-thumb,
.options-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}
.answers-container::-webkit-scrollbar-thumb:hover,
.options-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-xl .modal-dialog {
    max-width: 95%;
    margin: 1rem auto;
  }
  .question-block {
    padding: 1rem;
  }
  .survey-structure {
    padding: 1rem;
  }
  .user-list {
    height: 250px;
  }
  .stat-number {
    font-size: 1.25rem;
  }
  .answers-container {
    max-height: 50vh;
  }
  .answer-item {
    padding: 1rem;
  }
  .question-number {
    min-width: 1.5rem;
    height: 1.5rem;
    font-size: 0.75rem;
  }
  .rating-stars i {
    font-size: 1rem;
  }
  .stat-number {
    font-size: 1.5rem;
  }
  .user-info-header {
    padding: 1rem;
  }
  .survey-info-section {
    padding: 1rem;
  }
}

/* Print styles for individual answers */
@media print {
  .modal-header,
  .modal-footer {
    display: none !important;
  }
  .answer-item {
    page-break-inside: avoid;
    border: 1px solid #dee2e6;
    margin-bottom: 1rem;
  }
  .answers-container {
    max-height: none;
    overflow: visible;
  }
}

/* Toast Notification Styles */
.toast-container {
  z-index: 9999;
}

.toast {
  min-width: 300px;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  border: none;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
}

.toast.show {
  opacity: 1;
}

.toast-header {
  border-bottom: none;
  padding: 0.75rem 1rem;
}

.toast-body {
  padding: 0.75rem 1rem;
  font-weight: 500;
}

.bg-success .toast-header,
.bg-success .toast-body {
  background-color: #198754 !important;
}

.bg-danger .toast-header,
.bg-danger .toast-body {
  background-color: #dc3545 !important;
}
</style>
